import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import ConfirmSheet from '@/components/ConfirmSheet';
import { Plus, Package, Eye, Bookmark, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';

export default function FarmerDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, title}
  const [deleting, setDeleting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await api.get(`/farmer/${user._id}/produce`);
      setPosts(data.posts || []);
    } catch {
      toast.error('Failed to load your produce');
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleToggleAvailability = async (postId, currentState) => {
    try {
      await api.put(`/produce/${postId}`, { is_available: !currentState });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_available: !currentState } : p));
      toast.success(!currentState ? 'Marked as available' : 'Marked as sold out');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/produce/${deleteTarget.id}`);
      setPosts(prev => prev.filter(p => p.id !== deleteTarget.id));
      toast.success('Produce deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalSaves = posts.reduce((sum, p) => sum + (p.saves || 0), 0);

  return (
    <AppShell
      title="My Dashboard"
      headerRight={
        <Button
          data-testid="create-produce-btn"
          onClick={() => navigate('/create-produce')}
          size="sm"
          className="h-8 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full text-xs px-3"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New Post
        </Button>
      }
    >
      <div className="p-4 space-y-5" data-testid="farmer-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 animate-fade-in-up">
          <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
            <Package className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
            <p className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{posts.length}</p>
            <p className="text-[10px] text-[#5B6675]">Posts</p>
          </div>
          <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
            <Eye className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
            <p className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{totalViews}</p>
            <p className="text-[10px] text-[#5B6675]">Views</p>
          </div>
          <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
            <Bookmark className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
            <p className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{totalSaves}</p>
            <p className="text-[10px] text-[#5B6675]">Saves</p>
          </div>
        </div>

        {/* Posts List */}
        <div>
          <h3 className="text-base font-semibold text-[#0B1620] mb-3 font-['Bricolage_Grotesque']">Your Produce</h3>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 mb-3 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-[#EFF2F7] rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-[#EFF2F7] rounded w-3/4" />
                    <div className="h-3 bg-[#EFF2F7] rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))
          ) : posts.length === 0 ? (
            <div className="text-center py-12 bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF]">
              <Package className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
              <p className="text-sm text-[#5B6675]">No produce posted yet</p>
              <Button
                onClick={() => navigate('/create-produce')}
                className="mt-3 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full text-sm px-6"
              >
                <Plus className="w-4 h-4 mr-1" /> Create First Post
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post, i) => (
                <div
                  key={post.id}
                  data-testid={`dashboard-post-${post.id}`}
                  className={`bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 animate-fade-in-up ${!post.is_available ? 'opacity-75' : ''}`}
                  style={{ animationDelay: `${i * 0.05}s`, opacity: 0 }}
                >
                  <div className="flex gap-3">
                    {/* Thumbnail */}
                    <div className="relative w-16 h-16 rounded-lg bg-[#EFF2F7] overflow-hidden flex-shrink-0">
                      {post.images?.[0] ? (
                        <img src={post.images[0]} alt="" className={`w-full h-full object-cover ${!post.is_available ? 'grayscale' : ''}`} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🌾</div>
                      )}
                      {!post.is_available && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <span className="text-[8px] font-bold text-[#F6F8FB] tracking-wider rotate-[-10deg] border border-[#E4E8EF] px-1.5 py-0.5 rounded">
                            SOLD
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-[#0B1620] truncate">{post.title}</h4>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${
                          post.is_available ? 'bg-[#15803D]/10 text-[#15803D]' : 'bg-[#B91C1C]/10 text-[#B91C1C]'
                        }`}>
                          {post.is_available ? 'Available' : 'Sold Out'}
                        </span>
                      </div>
                      <p className="text-xs text-[#5B6675] mt-0.5">
                        ₹{post.price_per_unit}/{post.unit} · {post.quantity} {post.unit}
                      </p>
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#5B6675]">
                        <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {post.views}</span>
                        <span className="flex items-center gap-0.5"><Bookmark className="w-3 h-3" /> {post.saves}</span>
                      </div>
                    </div>
                  </div>

                  {/* Status toggle button (prominent) */}
                  <button
                    data-testid={`toggle-avail-${post.id}`}
                    onClick={() => handleToggleAvailability(post.id, post.is_available)}
                    className={`mt-3 w-full flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold transition-colors ${
                      post.is_available
                        ? 'bg-[#B91C1C]/5 text-[#B91C1C] hover:bg-[#B91C1C]/10 border border-[#B91C1C]/20'
                        : 'bg-[#15803D]/5 text-[#15803D] hover:bg-[#15803D]/10 border border-[#15803D]/20'
                    }`}
                  >
                    {post.is_available ? (
                      <>
                        <ToggleLeft className="w-4 h-4" />
                        Mark as Sold Out
                      </>
                    ) : (
                      <>
                        <ToggleRight className="w-4 h-4" />
                        Mark as Available
                      </>
                    )}
                  </button>

                  {/* Secondary actions */}
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#E4E8EF]">
                    <div className="flex-1" />
                    <button
                      data-testid={`edit-post-${post.id}`}
                      onClick={() => navigate(`/edit-produce/${post.id}`)}
                      className="flex items-center gap-1 text-xs text-[#5B6675] hover:text-[#0B4F3F] transition-colors px-2 py-1"
                    >
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      data-testid={`delete-post-${post.id}`}
                      onClick={() => setDeleteTarget({ id: post.id, title: post.title })}
                      className="flex items-center gap-1 text-xs text-[#5B6675] hover:text-[#B91C1C] transition-colors px-2 py-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <ConfirmSheet
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete this produce post?"
        description={deleteTarget ? `"${deleteTarget.title}" will be removed from the marketplace. This can't be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Keep it"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={handleDelete}
        loading={deleting}
        testId="delete-post"
      />
    </AppShell>
  );
}
